import { ReactElement, useMemo } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { updateFund } from '../../redux/pages/actions';
import {
  activeDateSelector,
  createActiveFundSelectorBySection,
} from '../../redux/pages/selectors';
import { clientNameSelector } from '../../redux/auth/selectors';
import { Autocomplete, TextField } from '@mui/material';
import { RaptorTheme } from '../../styling/theme';
import { useDemoFundIds } from '../../hooks/useDemoFundIds';
import Pill from '../ui/Pill';
import { cn } from '../../utilities/cn';
import { tailwindColors } from '../../../tailwind.config';

export const useFundPickerStyles = makeStyles<
  RaptorTheme,
  { isDemoSelected?: boolean }
>((theme) => ({
  root: {
    backgroundColor: ({ isDemoSelected }) =>
      isDemoSelected ? tailwindColors.demo_bg : theme.palette.grey[100],
    padding: '0.22rem',
    borderRadius: 8,
  },
  inputRoot: {
    paddingLeft: '0.31rem',
    color: theme.palette.primary.main,
  },
  option: {
    '&&': {
      display: 'block',
      overflow: 'visible',
      height: 'auto',
      whiteSpace: 'normal',
      alignItems: 'unset',
    },
  },
  demoOption: {
    backgroundColor: tailwindColors.demo_bg,
    '&&:hover, &&.Mui-focused, &&[aria-selected="true"]': {
      backgroundColor: '#e9d5ff',
    },
  },
}));

interface Props {
  labels: string[];
  values: string[];
  currentlySelected: string;
  section: string;
  currentPage: string;
  pageId?: string;
}

function FundPicker({
  currentlySelected,
  section,
  currentPage,
  pageId,
  ...props
}: Props) {
  const labels = section === 'esg' ? props.labels.slice(0, 22) : props.labels;
  const values = section === 'esg' ? props.values.slice(0, 22) : props.values;
  const clientName = useSelector(clientNameSelector) || 'mersenne';
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const activeFundSelector = createActiveFundSelectorBySection(section, pageId);
  const fundIdParam = useSelector(activeFundSelector);
  const activeDate = useSelector(activeDateSelector);

  const handleChange = (fundId: string | null | undefined) => {
    if (!fundId) {
      dispatch(updateFund(section, '', activeDate));
      navigate(`/${section}/${currentPage}`);
    } else {
      dispatch(updateFund(section, fundId, activeDate));
      if (activeDate !== '') {
        navigate(
          `/${section}/${currentPage}?fundId=${fundId as string
          }&positionDate=${activeDate}`,
        );
      } else {
        navigate(`/${section}/${currentPage}?fundId=${fundId as string}`);
      }
    }
  };

  const demoFundIds = useDemoFundIds(section, pageId);
  const selectedIsDemo = !!(
    fundIdParam?.id && demoFundIds.has(fundIdParam.id)
  );
  const classes = useFundPickerStyles({ isDemoSelected: selectedIsDemo });

  const options = useMemo(
    () =>
      labels
        .map((label, index) => ({
          label: clientName.includes('mersenne')
            ? label
            : `(${values[index]}) - ${label}`,
          value: values[index],
          isDemo: demoFundIds.has(values[index]),
        }))
        .sort((a, b) =>
          a.label.localeCompare(b.label, undefined, {
            numeric: true,
            sensitivity: 'base',
          }),
        ),
    [labels, values, clientName, demoFundIds],
  );

  return labels.length ? (
    <Autocomplete
      id="fund_picker"
      options={options}
      className="mr-2 w-[28rem] min-w-96 text-neutral-600"
      classes={{ option: classes.option }}
      onChange={(label, value) => handleChange(value?.value)}
      getOptionLabel={(option) => option?.label || ''}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      clearOnBlur={false}
      value={
        options.find((option) => option.value === fundIdParam?.id) || {
          label: '',
          value: '',
          isDemo: false,
        }
      }
      ChipProps={{
        onClick: () => {
          handleChange('none_selected');
        },
      }}
      renderOption={(props, option) => {
        const { key, className, ...optionProps } = props;
        return (
          <li
            key={key ?? option.value}
            {...optionProps}
            className={cn(className, option.isDemo && classes.demoOption)}
          >
            {option.isDemo ? (
              <Pill status="demo" className="float-right ml-2 mt-0.5">
                Demo
              </Pill>
            ) : null}
            {option.label}
          </li>
        );
      }}
      renderInput={(params): ReactElement => (
        <TextField
          {...params}
          variant="standard"
          InputLabelProps={{
            variant: 'standard',
          }}
          placeholder="Please select a fund"
          InputProps={{
            ...params.InputProps,
            classes: {
              root: classes.inputRoot,
            },
            disableUnderline: true,
          }}
          className={classes.root}
        />
      )}
    />
  ) : null;
}

export default FundPicker;
